import type {
  GetUserPropertyDetailsBuildingAttributesWebDto,
  UserPropertyBuildingAttributesAirConditioningTypesWebDto,
  UserPropertyBuildingAttributesApplianceTypesWebDto,
  UserPropertyBuildingAttributesCommunicationTypesWebDto,
  UserPropertyBuildingAttributesCommunityRoomTypesWebDto,
  UserPropertyBuildingAttributesFloorCoveringTypesWebDto,
  UserPropertyBuildingAttributesHeatingSourceTypesWebDto,
  UserPropertyBuildingAttributesLeaseLengthsWebDto,
  UserPropertyBuildingAttributesOutdoorCommonAreaTypesWebDto,
  UserPropertyBuildingAttributesParkingTypesWebDto,
  UserPropertyBuildingAttributesSecurityTypesWebDto,
  UserPropertyBuildingAttributesSportCourtTypesWebDto,
  UserPropertyBuildingAttributesUtilitiesIncludedWebDto,
  UserPropertyBuildingAttributesViewTypesWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { RecommendationsBySection } from '../hooks/queries/properties';

export type ContentSectionStatus = {
  isIncomplete: boolean;
  notStarted: boolean;
  completeStatusDisplay: string;
  recsKeys: Array<string>;
};

/**
 * Extract recommendation keys for a section
 * Replaces the repetitive pattern:
 * const recs = recommendations?.[SECTION]; const recsKeys = recs && Object.keys(recs);
 */
export const getRecommendationKeys = (
  recommendations: RecommendationsBySection | undefined,
  section: string,
): Array<string> => {
  const sectionRecs = recommendations?.[section];
  return sectionRecs ? Object.keys(sectionRecs) : [];
};

type BuildingAttributeDto =
  | UserPropertyBuildingAttributesHeatingSourceTypesWebDto
  | UserPropertyBuildingAttributesFloorCoveringTypesWebDto
  | UserPropertyBuildingAttributesAirConditioningTypesWebDto
  | UserPropertyBuildingAttributesApplianceTypesWebDto
  | UserPropertyBuildingAttributesCommunicationTypesWebDto
  | UserPropertyBuildingAttributesViewTypesWebDto
  | UserPropertyBuildingAttributesCommunityRoomTypesWebDto
  | UserPropertyBuildingAttributesSecurityTypesWebDto
  | UserPropertyBuildingAttributesSportCourtTypesWebDto
  | UserPropertyBuildingAttributesOutdoorCommonAreaTypesWebDto
  | UserPropertyBuildingAttributesUtilitiesIncludedWebDto
  | UserPropertyBuildingAttributesLeaseLengthsWebDto
  | UserPropertyBuildingAttributesParkingTypesWebDto
  | GetUserPropertyDetailsBuildingAttributesWebDto;

/**
 * Count checked features from checkbox options
 * Replaces the repetitive filtering pattern for features like:
 * heatingSource, floorCovering, appliance, etc.
 */
export const countCheckedFeatures = <T extends BuildingAttributeDto>(
  featureKeys: Array<keyof T>,
  featureObject: T,
): number => {
  return featureKeys.filter((key) => featureObject[key]).length;
};

/**
 * Build status with recommendations-first check
 * Consolidates the common pattern of:
 * 1. Check if recommendations exist -> return notStarted
 * 2. Check if count is 0 -> return notStarted (if not feed)
 * 3. Otherwise return complete with display text
 */
export const buildStatusWithRecs = ({
  recommendations,
  section,
  itemCount,
  isFeed,
  displayTextFn,
}: {
  recommendations?: RecommendationsBySection;
  section: string;
  itemCount: number;
  isFeed: boolean;
  displayTextFn: (count: number) => string;
}): ContentSectionStatus => {
  const recsKeys = getRecommendationKeys(recommendations, section);

  // Early return if recommendations exist
  if (recsKeys.length > 0) {
    return createContentSectionStatus({
      notStarted: true,
      recsKeys,
    });
  }

  // Not started if no items (except for feed)
  if (itemCount === 0) {
    return createContentSectionStatus({
      notStarted: !isFeed,
      recsKeys,
    });
  }

  // Complete with display text
  return createContentSectionStatus({
    completeStatusDisplay: displayTextFn(itemCount),
    recsKeys,
  });
};

/**
 * Create a ContentSectionStatus object
 * Helper for creating status objects with defaults
 */
export const createContentSectionStatus = ({
  isIncomplete = false,
  notStarted = false,
  completeStatusDisplay = '',
  recsKeys = [],
}: Partial<ContentSectionStatus> = {}): ContentSectionStatus => ({
  isIncomplete: Boolean(isIncomplete),
  notStarted: Boolean(notStarted),
  completeStatusDisplay: String(completeStatusDisplay),
  recsKeys,
});
